/* eslint-disable @typescript-eslint/no-explicit-any */
import { takeLatest, put, select } from 'redux-saga/effects';
import {
  STATUS_UPDATED,
  SELECT_QUEUE,
  TICKET_INSERTED
} from '../features/queues';
import {
  DEVICE_STATUSES_UPDATED,
  PROVIDER_MESSAGE_UPDATED
} from '../reducers/app';
import { appInitialStatusCompleted } from '../reducers/app';
import * as R from 'ramda';

import { getCBXSocket } from '../web-socket/client';
import { Queue } from '../types/queue';

function queueMapper(q: Queue) {
  return {
    ...q,
    size: q.tickets.length,
    tickets: q.tickets || []
  };
}

function* checkForTicketChanges(queue: Queue) {
  // @ts-ignore
  const oldQueues = yield select((s) => s.queues.connectedQueues);
  const oldQueue = R.find(R.propEq(queue.id, 'id'))(oldQueues);
  if (!oldQueue) {
    return;
  }
  const oldTickets = oldQueue.tickets;
  const newTickets = queue.tickets;
  for (const newTicket of newTickets) {
    const isNew = !R.any(R.propEq(newTicket.uuid, 'uuid'))(oldTickets);
    if (isNew) {
      yield put({ type: TICKET_INSERTED, queue, newTicket });
    }
  }
}

function* queueStatusUpdated(action: any) {
  try {
    // Defaulted, not destructured bare: this saga runs under `takeLatest`, so
    // a throw here does not fail one update — redux-saga tears down the root
    // saga and every other watcher with it, and the app stops responding to
    // the socket entirely until it is reloaded.
    const queues = action.queues || [];
    const booths = action.booths || [];
    const deviceStatuses = action.deviceStatuses || [];
    const { providerMessage } = action;
    const { deviceUUID } = getCBXSocket();
    const boothConfig = R.find(R.propEq(deviceUUID, 'uuid'))(booths);

    // An empty binding means "this booth serves nothing", which is not the
    // same as "no binding", so only a missing booth falls back to every queue.
    const assignedQueues = boothConfig ? boothConfig.queues || [] : queues;

    const mappedQueues = [];
    for (const aq of assignedQueues) {
      const q = R.find(R.propEq(aq.id, 'id'))(queues);
      if (q) {
        mappedQueues.push(queueMapper(q));
      }
    }

    const deviceStatusesFiltered = R.reject(R.propEq(deviceUUID, 'deviceUUID'))(
      deviceStatuses
    );

    yield put({
      type: DEVICE_STATUSES_UPDATED,
      deviceStatuses: deviceStatusesFiltered
    });
    yield put({ type: PROVIDER_MESSAGE_UPDATED, providerMessage });
    // @ts-ignore
    const firstStatusReceived = yield select(
      (s) => s.queues.firstStatusReceived
    );
    if (firstStatusReceived) {
      for (const q of mappedQueues) {
        yield checkForTicketChanges(q);
      }
    }

    yield put({ type: STATUS_UPDATED, queues: mappedQueues });
    const firstQueue = R.head(mappedQueues);
    if (firstQueue) {
      // @ts-ignore
      const queuesState = yield select((s) => s.queues);
      if (!queuesState.selectedQueueId) {
        yield put({ type: SELECT_QUEUE, queueId: firstQueue.id });
      }
    }
  } finally {
    // @ts-ignore
    const initialStatusRequestInProgress = yield select(
      (s) => s.app.initialStatusRequestInProgress
    );
    if (initialStatusRequestInProgress) {
      yield put(appInitialStatusCompleted());
    }
  }
}

export function* queueStatusSaga() {
  yield takeLatest('web-socket/booth-queues-status/cbx', queueStatusUpdated);
}
